import React, { useEffect, useState } from "react";
import "../../style/player.css";
import SpitialPlayer from "../../components/player/wrapper";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import httpService from "../../services/http.service";
import { getBillingApiRoute, getCMSApiRoute } from "../../utils/api.utils";
import { getAgeRange, isContentItemAllowedForAgeRange } from "../../utils/ageRange.util";
import AccessDeniedScreen from "../../components/common/accessDenied";
import _ from "lodash";
import RootLayout from "../layout";
import { Paper } from "../../components/legacy-ui/materialCompat";
import { loadAdvertisement } from "../../services/advertisementService";
import { hasAuthenticatedSession } from "../../services/authStorage";

const getConfigFromURL = async (configURL) => {
  try {
    const response = await fetch(configURL, {});

    const data = await response.json();
    const {
      source: sources,
      thumbnail,
      subTitles,
      subtitles,
      subtitle,
      poster,
    } = data;

    return {
      success: true,
      sources,
      thumbnail,
      poster,
      subTitles: subtitle || subTitles || subtitles,
    };
  } catch (error) {
    console.log("ConfigURL Fetch Error");
    console.log(error);
    return {
      success: false,
    };
  }
};

const isCinemaTicketPurchased = async (contentSlug, slug) => {
  try {
    const response = await httpService.get(
      getBillingApiRoute(
        "payment?" +
        new URLSearchParams({
          pageNumber: 1,
          pageIndex: 40,
          orderType: "cinema_ticket",
          status: 1,
          ticketKey: decodeURI(slug),
        })
      )
    );

    const { data } = response.data;

    if (response.status !== 200) return response.status;

    if (data?.results?.length !== 0) {
      const _data = await fetchCinemaPlayerResource(
        contentSlug,
        data?.results[0]?.traceCode
      );

      return _data;
    }
  } catch (error) {
    console.log(`SSR Error on CinemaTicket Get: ${error}`);
    return {};
  }
};

const fetchCinemaPlayerResource = async (slug, ticketTraceCode) => {
  try {
    const response = await httpService.get(
      getCMSApiRoute(
        "content/player-resource-cinema?" +
        new URLSearchParams({
          slug,
          ticketTraceCode,
        })
      )
    );

    const { data } = response.data;
    if (response.status !== 200) return response.status;

    return data;
  } catch (error) {
    console.log(`SSR Error on PlayerResource Get: ${error}`);
    return {};
  }
};

const getWatchTrackerData = async (contentKey, isEpisode, season, episode) => {
  try {
    const response = await httpService.get(
      getCMSApiRoute(
        "action/watch-tracker?" +
        new URLSearchParams(
          _.omitBy(
            {
              contentKey,
              isEpisode,
              season,
              episode,
            },
            _.isNil
          )
        ).toString()
      )
    );

    const { data } = response.data;

    // API returns { results: [...] } structure
    return data?.results?.[0] || data?.[0] || {};
  } catch (error) {
    console.log(`Error on SSR Watch Tracker get`);
    console.log(error);
    return {};
  }
};

async function getData(params, searchParams) {
  try {
    const { isTicketing } = searchParams;
    const decodedUrl = decodeURIComponent(params.slug);

    if (Number(isTicketing) === 1) {
      return await isCinemaTicketPurchased(
        params.slug,
        decodeURIComponent(searchParams.contentSlug)
      );
    } else {
      const response = await httpService.get(
        getCMSApiRoute(
          "content/player-resource?" +
          new URLSearchParams({
            slug: decodedUrl,
          })
        )
      );

      const { data } = response.data;

      if (response.status !== 200) return response.status;

      return data;
    }
  } catch (error) {
    console.log(error);
    return {};
  }
}

async function getMoviesData(params, searchParams) {
  try {
    const data = await getData(params, searchParams);

    if (data === 400)
      return {
        rawData: 400,
      };

    if (!data) {
      return {
        movie: "",
        vast: "",
        hasErr: true,
        message: "محتوا با مشخصات مورد نظر یافت نشد",
      };
    }

    if (
      data?.contentType?.kind === "series" ||
      data?.contentType?.kind === "program"
    ) {
      const behavior = data?.behavior;
      const { s, e } = searchParams;

      if (!s || !e)
        return {
          movie: "",
          vast: "",
          hasErr: true,
          message: "اطلاعات محتوای مشخص شده نامعتبر می باشد",
        };

      const episodeItem = data?.contentType.seriesInfo.find(
        (x) => +x.episodeNumber === +e && +x.seasonNumber === +s
      );

      if (!episodeItem)
        return {
          movie: "",
          vast: "",
          hasErr: true,
          message: "قسمت مورد نظر یافت نشد",
        };

      const watchTrackData = await getWatchTrackerData(params.slug, 1, +s, +e);
      const posters = episodeItem?.data?.meta?.contentPhotos?.filter(
        (x) => x.type === "videoPlayerCover"
      );
      const hotpoints = episodeItem?.data?.meta?.hotpoints;
      let { sources, thumbnail, subTitles, configURL } = episodeItem.data;

      if (!sources && !configURL) {
        return {
          movie: "",
          vast: "",
          hasErr: true,
          message: "EPISODE_SOURCE_NOT_FOUND",
        };
      }

      if (configURL) {
        const confData = await getConfigFromURL(configURL);
        if (!confData.success) {
          return {
            movie: "",
            vast: "",
            hasErr: true,
            message: "منبعی برای پخش تعریف نشده",
          };
        } else {
          sources = confData.sources;
          thumbnail = confData.thumbnail;
          subTitles = confData.subTitles;
        }
      }

      const { ads } = data;
      const vast = await loadAdvertisement(
        params.slug,
        searchParams.campaignKey
      );

      return {
        movie: { sources, thumbnail, subTitles },
        vast,
        hotPoints: hotpoints || [],
        ads,
        hasErr: false,
        posters,
        behavior,
        rawData: data,
        watchTrackData,
      };
    } else {
      const hotpoints = data?.meta?.hotpoints;
      let {
        sources,
        ads,
        thumbnail,
        subTitles,
        configURL,
        isTicketing,
        isLive,
        behavior,
      } = data;

      if (configURL) {
        const confData = await getConfigFromURL(configURL);

        if (!confData.success) {
          return {
            movie: "",
            vast: "",
            hasErr: true,
            message: "منبعی برای پخش تعریف نشده",
          };
        } else {
          sources = confData.sources;
          thumbnail = confData.thumbnail;
          subTitles = confData.subTitles;
        }
      }

      const watchTrackData = await getWatchTrackerData(params.slug);
      const posters = data?.contentPhotos?.filter(
        (x) => x.type === "videoPlayerCover"
      );
      const vast = await loadAdvertisement(
        params.slug,
        searchParams.campaignKey
      );

      return {
        movie: { sources, thumbnail, subTitles },
        vast,
        ads,
        posters,
        hotPoints: hotpoints || [],
        hasErr: false,
        isLive,
        isTicketing,
        behavior,
        rawData: data,
        watchTrackData,
      };
    }
  } catch (error) {
    console.log(error);
    console.log(`Error on Player SSR Init: ${error}`);
    return {
      message: error.message,
      hasErr: true,
    };
  }
}

const checkAuth = () => hasAuthenticatedSession();

const MoviePlayer = () => {
  const auth = checkAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const campaignKey = searchParams.get("campaignKey") || undefined;
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const loginPath = `/login?ref=${encodeURIComponent(location.pathname)}`;

  useEffect(() => {
    if (!auth) {
      navigate(loginPath, {
        replace: true,
      });
    }
  }, [auth, loginPath, navigate]);

  useEffect(() => {
    if (!auth) return undefined;

    let active = true;
    const requestParams = {
      slug: params.slug,
      s: params.s,
      e: params.e,
      isTicketing: params.isTicketing,
      contentSlug: params.contentSlug,
      campaignKey,
    };
    setLoading(true);
    getMoviesData(requestParams, requestParams).then((data) => {
      if (!active) return;
      setData(data);
      setLoading(false);
    });
    return () => {
      active = false;
    };
  }, [
    auth,
    params.slug,
    params.s,
    params.e,
    params.isTicketing,
    params.contentSlug,
    campaignKey,
  ]);

  if (!auth) return null;

  if (loading)
    return (
      <main className="legacy-player-loading-screen">
        <div className="legacy-player-loading-cell">
          <img src="/three-dots.svg" alt="loading..." />
        </div>
      </main>
    );

  if (data && data.rawData) {
    const token = localStorage.getItem("webTvUserToken");
    const ageRange = getAgeRange(token);

    if (
      data.rawData === 400 ||
      !ageRange ||
      !isContentItemAllowedForAgeRange(data.rawData, ageRange, true)
    ) {
      return <AccessDeniedScreen />;
    } else {
      return (
        <RootLayout>
          <main className="w-full" dir="ltr">
            <SpitialPlayer
              key={`${params.slug || ""}:${params.s || ""}:${params.e || ""}`}
              data={data}
            />
          </main>
        </RootLayout>
      );
    }
  } else {
    return (
      <main className="w-full h-screen flex items-start md:items-center justify-center bg-black dark">
        <Paper className="p-5">
          {
            "خطا در دریافت اطلاعات محتوا، لطفا اتصال اینترنت خود را بررسی کرده و سپس مجددا تلاش فرمائید"
          }
        </Paper>
      </main>
    );
  }
};

export default MoviePlayer;
